import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Badge } from 'antd';
import { ArrowLeftOutlined, DatabaseOutlined } from '@ant-design/icons';
import DismissibleAlert from '../../components/DismissibleAlert';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import DashboardSection from '../dashboard/components/DashboardSection';
import StaffUploadsPanel from './components/StaffUploadsPanel';
import usePendingStaffUploadsCount from './hooks/usePendingStaffUploadsCount';
import { canManageStaffUploads } from './planRequestHelpers';
import { canViewBulkUploadLogs } from '../../components/CarePrograms/careProgramPermissions';
import './orgs-dashboard.css';

export const STAFF_UPLOADS_CONTEXT_BANNER_KEY = 'orgs-staff-uploads-context-v1';

/**
 * Cross-company staff upload inbox — still inside the Organizations product.
 * Day-to-day review: open the company → Members → Staff uploads.
 */
export default function StaffUploadsQueue() {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canReview = useMemo(
    () => canManageStaffUploads(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const canSeeHistory = useMemo(
    () => canViewBulkUploadLogs(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const { count: pendingCount, refresh } = usePendingStaffUploadsCount(
    Boolean(user && canReview),
  );

  const filterOrgId = searchParams.get('org_id') ? Number(searchParams.get('org_id')) : null;
  const filterOrgLabel = searchParams.get('org_name') || '';
  const filterSocialNo = searchParams.get('org_social_no') || null;

  return (
    <div className="orgs-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <p className="org-detail-breadcrumb">
              <Button
                type="link"
                className="org-detail-breadcrumb__link"
                onClick={() => navigate('/corporations')}
              >
                Organizations
              </Button>
              <span aria-hidden>/</span>
              <span>Staff uploads</span>
            </p>
            <h1 className="dash-masthead__title">
              Staff uploads across companies
              {pendingCount > 0 ? (
                <Badge
                  count={pendingCount}
                  overflowCount={99}
                  style={{ marginLeft: 12, verticalAlign: 'middle' }}
                />
              ) : null}
            </h1>
            <p className="dash-masthead__meta">
              Cross-company inbox for HR uploads waiting on care admin.
              To work inside one company, open it from the directory → Members → Staff uploads.
            </p>
          </div>
          <div className="dash-masthead__actions">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/corporations')}>
              Back to directory
            </Button>
          </div>
        </header>

        <DismissibleAlert
          bannerKey={STAFF_UPLOADS_CONTEXT_BANNER_KEY}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="You are still in Organizations"
          description={
            filterOrgLabel
              ? `Filtered to ${filterOrgLabel}. Clear the org filter to see all companies, or open that company for full context.`
              : 'Each row belongs to a company. Filter by organization, or open the company from the directory when you need profile, plans, and members together.'
          }
        />

        <DashboardSection
          id="orgs-staff-uploads-queue"
          icon={<DatabaseOutlined />}
          title="Pending and recent uploads"
          subtitle="Approve staged HR uploads before member records change"
          collapsible={false}
          variant="content"
        >
          <StaffUploadsPanel
            showOrgFilter
            filterOrgId={filterOrgId}
            filterOrgLabel={filterOrgLabel}
            filterSocialNo={filterSocialNo}
            onPendingCountChange={refresh}
            showApprovalQueue={canReview}
            showUploadHistory={canSeeHistory}
            onOrgFilterChange={(selectedOrg) => {
              if (selectedOrg?.org_id) {
                const next = new URLSearchParams({
                  org_id: String(selectedOrg.org_id),
                  org_name: selectedOrg.org_name || '',
                });
                const social = selectedOrg.org_social_no || selectedOrg.social_no;
                if (social) next.set('org_social_no', social);
                setSearchParams(next, { replace: true });
              } else {
                setSearchParams({}, { replace: true });
              }
            }}
          />
        </DashboardSection>
      </div>
    </div>
  );
}
